import { dateLib, propertyLib, roomLib } from "@agentcall/core/lib"
import { useQueryClient } from "@tanstack/react-query"
import { Suspense, useEffect, useState } from "react"
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  Book,
  Edit,
  FileText,
  MoreHorizontal,
  Plus,
  Sliders,
  Trash,
} from "react-feather"
import { Link, useLocation, useNavigate, useParams } from "react-router-dom"
import { toast } from "sonner"
import { propertyApi } from "@/api/client/property"
import { getPropertyGetQueryKey, useRoomDelete } from "@/api/generated/endpoints/api"
import type { PropertyGet200PropertyRoomsItem } from "@/api/generated/models"
import { getErrorMessage } from "@/api/instance"
import { EchoesSyncCard, EchoesSyncCell } from "@/components/echoes/EchoesSyncCard"
import { Button } from "@/components/ui/button"
import { EmptyCard } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu/DropdownMenu"
import { ReactErrorBoundary } from "@/components/ui/error-boundary"
import { ImagePreviewDialog, type PreviewImage } from "@/components/ui/image-preview-dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Pagination,
  PaginationContent,
  PaginationFirst,
  PaginationItem,
  PaginationLast,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination"
import { Spinner } from "@/components/ui/spinner/Spinner"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { usePermission } from "@/features/auth/hooks/usePermission"
import { openInNewTab } from "@/lib/navigation"

type OwnerMemoFile = {
  id: number
  url: string
}

const isOwnerMemoFile = (file: unknown): file is OwnerMemoFile => {
  if (typeof file !== "object" || file === null) {
    return false
  }

  const record = file as Record<string, unknown>
  return typeof record.id === "number" && typeof record.url === "string"
}

export function PropertyDetailPage() {
  const { propertyId } = useParams<{ propertyId: string }>()
  const navigate = useNavigate()

  if (!propertyId) {
    return <div>物件IDが指定されていません</div>
  }

  return (
    <ReactErrorBoundary>
      <Suspense fallback={<PropertyDetailPageFallback />}>
        <PropertyDetailPageContent propertyId={propertyId} navigate={navigate} />
      </Suspense>
    </ReactErrorBoundary>
  )
}

interface PropertyDetailPageContentProps {
  propertyId: string
  navigate: ReturnType<typeof useNavigate>
}

type RoomSortOrder = "asc" | "desc"

function PropertyDetailPageContent({ propertyId, navigate }: PropertyDetailPageContentProps) {
  const location = useLocation()
  const { canUpdate, canDelete } = usePermission()
  const [roomSearchInput, setRoomSearchInput] = useState("")
  const [roomSearch, setRoomSearch] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [roomSortOrder, setRoomSortOrder] = useState<RoomSortOrder>("asc")
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [selectedRoom, setSelectedRoom] = useState<PropertyGet200PropertyRoomsItem | null>(null)
  const [previewImage, setPreviewImage] = useState<PreviewImage | null>(null)
  const itemsPerPage = 20

  // APIからデータをフェッチ
  const { data, refetch } = propertyApi.usePropertyGetSuspense(propertyId, {
    withOwner: "true",
    withRooms: "true",
    withRoomsTenants: "true",
    withRoomsPlans: "true",
    withRoomsOwnerMemos: "true",
    withRoomsOwnerMemosFiles: "true",
  })
  const property = data.property
  const rooms = property.rooms || []
  const ownerMemos = rooms.flatMap((room) =>
    (room.ownerMemos ?? []).map((ownerMemo) => ({
      ...ownerMemo,
      roomName: room.name,
      files: (ownerMemo.files ?? []).filter(isOwnerMemoFile),
    }))
  )
  const isEchoesOwnerProperty = Boolean(property.owner?.echoesOwnerId)

  // ページに戻ってきた時にデータを再取得
  useEffect(() => {
    const state = location.state as { refetch?: boolean } | null
    if (state?.refetch) {
      refetch()
    }
  }, [location.state, refetch])

  // 検索ボタン押下時の処理
  const handleSearch = () => {
    setRoomSearch(roomSearchInput)
    setCurrentPage(1) // 検索時はページを1にリセット
  }

  // 削除ボタンクリック時の処理
  const handleDeleteClick = (room: PropertyGet200PropertyRoomsItem) => {
    setSelectedRoom(room)
    setIsDeleteModalOpen(true)
  }

  // 部屋番号で絞り込み
  const filteredRooms = rooms.filter((room) => (roomSearch ? room.name.includes(roomSearch) : true))
  const sortedRooms = [...filteredRooms].sort((a, b) => {
    const sortResult = a.name.localeCompare(b.name, "ja", { numeric: true })
    return roomSortOrder === "asc" ? sortResult : -sortResult
  })

  // ページネーション計算
  const totalCount = filteredRooms.length
  const totalPages = Math.ceil(totalCount / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const paginatedRooms = sortedRooms.slice(startIndex, startIndex + itemsPerPage)

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const renderPageNumbers = () => {
    const pages = []
    const maxVisiblePages = 5
    const halfRange = Math.floor(maxVisiblePages / 2)

    let startPage = Math.max(1, currentPage - halfRange)
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1)

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1)
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <PaginationItem key={i}>
          <PaginationLink
            onClick={(e) => {
              e.preventDefault()
              handlePageChange(i)
            }}
            isActive={currentPage === i}
            className="cursor-pointer"
          >
            {i}
          </PaginationLink>
        </PaginationItem>
      )
    }
    return pages
  }

  return (
    <div className="space-y-6">
      {/* Header - 戻るボタン */}
      <div className="flex items-center gap-2.5">
        <Button
          variant="ghost"
          size="icon"
          className="w-7 h-7 shadow-sm"
          onClick={() => navigate("/properties")}
        >
          <ArrowLeft size={14} />
        </Button>
        <h2 className="text-sm font-semibold text-slate-600">物件一覧に戻る</h2>
      </div>

      {/* Title & Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">{property.name || "物件詳細"}</h1>
        </div>
        <div className="flex gap-2.5">
          <Button
            variant="outline"
            onClick={() => {
              openInNewTab(`/tickets?propertyName=${encodeURIComponent(property.name)}`)
            }}
          >
            物件の案件を表示
          </Button>
          <Button
            disabled={isEchoesOwnerProperty}
            title={
              isEchoesOwnerProperty ? "Echoes連携物件の部屋はEchoes側で管理されます" : undefined
            }
            onClick={() => {
              if (isEchoesOwnerProperty) return
              openInNewTab(`/properties/${propertyId}/rooms/add`)
            }}
          >
            <Plus className="mr-2" size={16} />
            新規部屋追加
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="space-y-3.5">
        {/* 概要 Card */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="flex items-center justify-between px-5 py-2.5 border-b border-slate-200">
            <div className="flex items-center gap-1.5">
              <Book size={14} />
              <span className="text-sm font-semibold text-slate-600">概要</span>
            </div>
            {canUpdate("properties") && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  openInNewTab(`/properties/${propertyId}/edit`)
                }}
              >
                <Edit size={14} className="mr-2" />
                編集
              </Button>
            )}
          </div>
          <div className="p-5">
            <div className="grid grid-cols-7 gap-x-7.5 gap-y-4">
              {/* 1行目 */}
              <div>
                <div className="text-xs font-medium text-slate-400 mb-2">物件ID</div>
                <div className="text-sm text-slate-600">{property.id}</div>
              </div>
              <div>
                <div className="text-xs font-medium text-slate-400 mb-2">住所</div>
                <div className="text-sm text-slate-600">
                  {property.prefecture}
                  {property.city}
                  {property.town}
                  {property.district}
                </div>
              </div>
              <div>
                <div className="text-xs font-medium text-slate-400 mb-2">築年</div>
                <div className="text-sm text-slate-600">{property.builtYear}年</div>
              </div>
              <div>
                <div className="text-xs font-medium text-slate-400 mb-2">構造</div>
                <div className="text-sm text-slate-600">
                  {propertyLib.structureLabel(property.structure)}
                </div>
              </div>
              <div>
                <div className="text-xs font-medium text-slate-400 mb-2">用法</div>
                <div className="text-sm text-slate-600">{propertyLib.typeLabel(property.type)}</div>
              </div>
              <div className="col-span-2" />

              {/* 2行目 */}
              <div>
                <div className="text-xs font-medium text-slate-400 mb-2">階数</div>
                <div className="text-sm text-slate-600">{property.floor}階建て</div>
              </div>
              <div>
                <div className="text-xs font-medium text-slate-400 mb-2">建物管理会社</div>
                <div className="text-sm text-slate-600">{property.managementCompanyName}</div>
              </div>
              <div>
                <div className="text-xs font-medium text-slate-400 mb-2">建物管理会社電話番号</div>
                <div className="text-sm text-slate-600">{property.managementCompanyPhone}</div>
              </div>
              <div className="col-span-3" />
            </div>
          </div>
        </div>

        <EchoesSyncCard
          idLabel="Echoes建物ID"
          echoesId={property.echoesBuildingId}
          echoesUpdatedAt={property.echoesUpdatedAt}
          echoesFailedAt={property.echoesFailedAt}
        />

        {/* 物件注意事項 Card */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="flex items-center justify-between px-5 py-2.5 border-b border-slate-200">
            <div className="flex items-center gap-1.5">
              <AlertCircle className="w-3 h-3" />
              <span className="text-sm font-semibold text-slate-600">物件注意事項</span>
            </div>
            {canUpdate("properties") && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  openInNewTab(`/properties/${propertyId}/edit`)
                }}
              >
                <Edit size={14} className="mr-2" />
                編集
              </Button>
            )}
          </div>
          <div className="p-5">
            <div className="text-sm text-slate-600">{property.note || "注意事項はありません"}</div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <form
          onSubmit={(e) => {
            e.preventDefault()
            handleSearch()
          }}
          className="flex gap-4 items-end"
        >
          <div className="flex-1 max-w-[265px]">
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">
              部屋番号検索
            </label>
            <Input
              type="text"
              placeholder="部屋番号を入力"
              value={roomSearchInput}
              onChange={(e) => setRoomSearchInput(e.target.value)}
              className="h-10"
            />
          </div>
          <Button type="submit">検索</Button>
        </form>
      </div>

      {/* Rooms List */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="text-sm text-slate-400">{totalCount}件の部屋が見つかりました</div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="outline" size="sm">
                <Sliders className="mr-2" size={16} />
                表示順: 部屋番号 {roomSortOrder === "asc" ? "昇順" : "降順"}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[180px]">
              <DropdownMenuItem
                onClick={() => {
                  setRoomSortOrder("asc")
                  setCurrentPage(1)
                }}
              >
                部屋番号 昇順
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setRoomSortOrder("desc")
                  setCurrentPage(1)
                }}
              >
                部屋番号 降順
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="bg-white rounded-lg shadow-sm">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="whitespace-nowrap w-[250px]">物件名</TableHead>
                <TableHead className="whitespace-nowrap w-[150px]">部屋番号</TableHead>
                <TableHead className="whitespace-nowrap w-[150px]">賃借人名</TableHead>
                <TableHead className="whitespace-nowrap w-[150px]">契約開始日</TableHead>
                <TableHead className="whitespace-nowrap w-[150px]">契約終了日</TableHead>
                <TableHead className="whitespace-nowrap w-[150px]">サービス利用状況</TableHead>
                <TableHead className="whitespace-nowrap">Echoes連携</TableHead>
                <TableHead className="whitespace-nowrap">案件を表示</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedRooms.map((room) => {
                const tenant = roomLib.roomCurrentTenant(room)
                const tenantName = tenant?.name || "未入居"
                const contractStartDate = tenant?.startDate || "-"
                const contractEndDate = tenant?.endDate || "-"

                const {
                  label: planLabel,
                  variant: planVariant,
                  suspended: planSuspended,
                } = roomLib.getRoomServicePlanInfo(room)

                return (
                  <TableRow key={room.id}>
                    <TableCell>{property.name}</TableCell>
                    <TableCell>
                      <Link
                        to={`/properties/${propertyId}/rooms/${room.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                      >
                        {room.name}
                      </Link>
                    </TableCell>
                    <TableCell className={tenantName === "未入居" ? "text-slate-400" : ""}>
                      {tenantName}
                    </TableCell>
                    <TableCell>{contractStartDate}</TableCell>
                    <TableCell>{contractEndDate}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Label variant={planVariant}>{planLabel}</Label>
                        {planSuspended && <Label variant="planSuspended">停止中</Label>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <EchoesSyncCell
                        echoesId={room.echoesResidenceId}
                        echoesFailedAt={room.echoesFailedAt}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          openInNewTab(
                            `/tickets?propertyName=${encodeURIComponent(property.name)}&roomName=${encodeURIComponent(room.name)}`
                          )
                        }
                      >
                        案件を表示
                      </Button>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal size={16} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="min-w-[200px]">
                          <DropdownMenuItem
                            onClick={() =>
                              openInNewTab(`/properties/${propertyId}/rooms/${room.id}`)
                            }
                          >
                            <Book className="text-slate-600 mr-2" size={16} />
                            詳細
                          </DropdownMenuItem>
                          {canUpdate("rooms") && (
                            <DropdownMenuItem
                              onClick={() =>
                                openInNewTab(`/properties/${propertyId}/rooms/${room.id}/edit`)
                              }
                            >
                              <Edit className="text-slate-600 mr-2" size={16} />
                              編集
                            </DropdownMenuItem>
                          )}
                          {canDelete("rooms") && <DropdownMenuSeparator />}
                          {canDelete("rooms") && (
                            <DropdownMenuItem
                              onClick={() => handleDeleteClick(room)}
                              className="text-red-600"
                            >
                              <Trash className="text-slate-600 mr-2" size={16} />
                              削除
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>

        {totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between">
            <div className="text-sm text-slate-500">
              {totalCount} 件中 {(currentPage - 1) * itemsPerPage + 1} -{" "}
              {Math.min(currentPage * itemsPerPage, totalCount)} 件を表示
            </div>
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationFirst
                    onClick={(e) => {
                      e.preventDefault()
                      handlePageChange(1)
                    }}
                    disabled={currentPage === 1}
                    className="cursor-pointer"
                  />
                </PaginationItem>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={(e) => {
                      e.preventDefault()
                      handlePageChange(currentPage - 1)
                    }}
                    disabled={currentPage === 1}
                    className="cursor-pointer"
                  />
                </PaginationItem>
                {renderPageNumbers()}
                <PaginationItem>
                  <PaginationNext
                    onClick={(e) => {
                      e.preventDefault()
                      handlePageChange(currentPage + 1)
                    }}
                    disabled={currentPage === totalPages}
                    className="cursor-pointer"
                  />
                </PaginationItem>
                <PaginationItem>
                  <PaginationLast
                    onClick={(e) => {
                      e.preventDefault()
                      handlePageChange(totalPages)
                    }}
                    disabled={currentPage === totalPages}
                    className="cursor-pointer"
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </div>

      {/* Owner Memos */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="flex items-center justify-between px-5 py-2.5 border-b border-slate-200">
          <div className="flex items-center gap-1.5">
            <FileText size={14} />
            <span className="text-sm font-semibold text-slate-600">オーナーメモ</span>
          </div>
          <div className="h-9" />
        </div>
        <div className="p-5">
          {ownerMemos.length > 0 ? (
            <div className="grid grid-cols-1 gap-4">
              {ownerMemos.map((ownerMemo) => (
                <div
                  key={ownerMemo.id}
                  className="border border-slate-200 rounded-md p-3.5 h-full flex flex-col"
                >
                  <div className="flex gap-3.5 flex-1">
                    <div className="flex-1 space-y-1.5">
                      <div className="text-xs font-medium text-slate-400">
                        {dateLib.formatDateTime(ownerMemo.createdAt)}
                      </div>
                      <div className="text-xs font-medium text-slate-400">
                        部屋番号: {ownerMemo.roomName}
                      </div>
                      <div className="text-sm text-slate-600">{ownerMemo.title}</div>
                      <div className="text-sm text-slate-500 whitespace-pre-wrap">
                        {ownerMemo.body}
                      </div>
                    </div>
                    <div className="flex gap-4">
                      {ownerMemo.files.map((file) => (
                        <button
                          type="button"
                          key={file.id}
                          onClick={() =>
                            setPreviewImage({
                              url: file.url,
                              title: ownerMemo.title,
                            })
                          }
                          className="w-[188px] h-[141px] rounded-md bg-slate-200 bg-cover bg-center flex-shrink-0 cursor-zoom-in"
                          style={{ backgroundImage: `url(${file.url})` }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyCard text="オーナーメモはありません" />
          )}
        </div>
      </div>

      <DeleteRoomModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        room={selectedRoom}
        propertyId={propertyId}
      />
      <ImagePreviewDialog image={previewImage} onClose={() => setPreviewImage(null)} />
    </div>
  )
}

interface DeleteRoomModalProps {
  isOpen: boolean
  onClose: () => void
  room: PropertyGet200PropertyRoomsItem | null
  propertyId: string
}

function DeleteRoomModal({ isOpen, onClose, room, propertyId }: DeleteRoomModalProps) {
  const { mutateAsync: deleteRoom, isPending } = useRoomDelete({
    mutation: {
      onError: (error) =>
        toast.error("削除に失敗しました", { description: getErrorMessage(error) }),
    },
  })
  const queryClient = useQueryClient()

  const handleDelete = async () => {
    if (!room) return
    try {
      await deleteRoom({ id: String(room.id) })
      onClose()
      // キャッシュを無効化して再フェッチ
      queryClient.invalidateQueries({ queryKey: getPropertyGetQueryKey(String(propertyId)) })
      toast.success("部屋を削除しました")
    } catch {
      // 失敗の表示は mutation の onError が担当する
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="text-red-500" size={20} />
            本当に削除してよろしいですか？
          </DialogTitle>
        </DialogHeader>
        <DialogDescription>
          部屋「{room?.name}」を削除します。削除した部屋は復元できません。
        </DialogDescription>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
            キャンセル
          </Button>
          <Button type="button" variant="destructive" onClick={handleDelete} disabled={isPending}>
            {isPending ? "削除中..." : "削除する"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function PropertyDetailPageFallback() {
  return (
    <div className="p-10 flex flex-col items-center justify-center">
      <Spinner className="text-slate-600 size-10 transition-opacity" />
    </div>
  )
}
